import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { getChartPalette, chartStateShell } from "./chartTheme";

type SlotChartProps = {
  title: string;
  xLabel: string;
  yLabel: string;
  data: any;
  isFetching?: boolean;
  isDarkMode?: boolean;
};

const SlotChart = ({
  title,
  xLabel,
  yLabel,
  data,
  isFetching,
  isDarkMode = true,
}: SlotChartProps) => {
  const formattedData = useMemo(
    () => (typeof data === "object" && data !== null ? data : {}),
    [data]
  );

  const pieData = useMemo(() => {
    const slotWise = formattedData?.slot_wise || { categories: [], series: [], numbers: [] };
    const categories = slotWise.categories || [];
    const series = slotWise.series || [];
    const numbers = slotWise.numbers || [];

    // Check if categories array is empty or has length <= 0
    if (!categories || categories.length <= 0) return [];

    // Check if series array is empty or has length <= 0
    if (!series || series.length <= 0) return [];

    return categories.map((cat: string, i: number) => ({
      name: cat,
      value: series[i] ?? 0, // percentage value
      raw: numbers[i] ?? 0, // actual number
    }));
  }, [formattedData]);

  // Check if response is successful but categories/series are empty or all values are zero
  const isNoDataFound = useMemo(() => {
    const slotWise = formattedData?.slot_wise || { categories: [], series: [], numbers: [] };
    const categories: string[] = slotWise.categories || [];
    const series: number[] = slotWise.series || [];
    
    // If categories array length is 0 or less OR series array length is 0 or less, show "No data found"
    if (categories.length <= 0 || series.length <= 0) return true;
    
    // If all series values are 0, also show "No data found"
    if (series.every(value => value === 0)) return true;
    
    return false;
  }, [formattedData]);

  const option = useMemo(() => {
    const c = getChartPalette(isDarkMode);
    return {
      chartOption: {
        tooltip: {
          trigger: "item",
          formatter: (params: any) => {
            return `
              <b>${params.name}</b><br/>
              ${params.value}% (${params.data.raw})
            `;
          },
        },
        legend: {
          bottom: 0,
          left: "center",
          data: pieData.map((d:any) => d.name),
          textStyle: {
            fontFamily: "Arial",
            color: c.secondaryTextColor,
            fontSize: 12,
          },
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 10,
        },
        toolbox: {
          feature: {
            saveAsImage: { title: "Save", name: title },
          },
          iconStyle: { borderColor: c.borderColor },
        },
        series: [
          {
            name: title,
            type: "pie",
            radius: "70%",
            avoidLabelOverlap: true,
            itemStyle: {
              borderColor: c.backgroundColor,
              borderWidth: 0,
            },
            emphasis: {
              focus: false,
              label: {
                show: true,
                fontWeight: "bold",
                fontFamily: "Arial",
              },
            },
            label: {
              show: true,
              position: "outside",
              formatter: (params: any) => `${params.name}: ${params.value}%`,
              fontSize: 11,
              color: c.labelColor,
              fontWeight: "normal",
              fontFamily: "Arial",
              distance: 5,
              backgroundColor: c.pieLabelBg,
              padding: [2, 4],
            },
            labelLine: {
              show: true,
              length: 10,
              length2: 5,
            },
            data: pieData,
          },
        ],
        color: c.colors,
      },
    };
  }, [title, pieData, isDarkMode]);

  // Show loading message when request is going to backend/response not coming yet
  if (isFetching) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Loading chart data...
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Calculating slot-wise distribution for this view.
          </p>
        </div>
      </div>
    );
  }

  // Show "no data" message when response comes successfully but categories/series are empty
  if (isNoDataFound) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No data found for this chart.
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Try selecting a different time range or slot filter.
          </p>
        </div>
      </div>
    );
  }

  return (
<div style={{ height: '58vh', width: '100%' }}>
    <ReactECharts
      option={option.chartOption}
      style={{ height: '98%', width: "100%" }}
      opts={{ renderer: "canvas" }}
      className="chart-container no-hover"
    />
    </div>
  );
};

export default SlotChart;
